import React, { useState, FC, useRef, RefObject, useMemo, useEffect, } from 'react';
import {
    Button, TextField, Grid, FormControl,
    Dialog, DialogContent, DialogTitle, DialogActions,
    Paper, IconButton, Box, Typography, TableContainer, Table, TableHead,
    TableRow, TableCell, TableBody, InputAdornment, Radio,
    RadioGroup, FormControlLabel, Autocomplete
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { PaperProps } from "@mui/material/Paper";
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { normalizeString } from 'utils/Validations'
import { AccrualProfileCreate } from 'repository/timekeeper/@types/AccrualProfile';
import { TimekeeperService } from 'services/TimekeeperService';
import { AlertCustom } from 'shared/ui/alert/AlertCustom';


interface CreateProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: any;
    reloading: () => void;
}

function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable
            nodeRef={nodeRef as RefObject<HTMLDivElement>}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

// Schema de validación Zod
const createAccrualProfileSchema = (t: any, rows: any[]) => {
    return z.object({
        name: z.string()
            .transform(val => val.trim())
            .pipe(
                z.string()
                    .min(2, { message: t('general.zod.string.min_chars', { min: 2 }) })
                    .max(100, { message: t('general.zod.string.max_chars', { max: 100 }) })
                    .refine(
                        (val) => /^[a-zA-Z0-9\s\u00e1\u00e9\u00ed\u00f3\u00fa\u00c1\u00c9\u00cd\u00d3\u00da\u00f1\u00d1\u00fc\u00dc\-_]+$/.test(val),
                        { message: t('general.zod.string.invalid_characters') }
                    )
                    .refine(
                        (val) => !rows.some((row: any) => normalizeString(row.name) === normalizeString(val)),
                        { message: t('accruals.accrual_profile.dialog.alerts.name_exist') }
                    )
            ),
        description: z.string().max(255, { message: t('general.zod.string.max_chars', { max: 255 }) }).optional(),
        accrual_code_id: z.number().nullable()
    });
};

export const CreateAccrualProfile: FC<CreateProps> = ({ open, setOpen, rows, reloading }) => {

    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar();

    // Schema con validaciones dinámicas
    const schema = useMemo(() => createAccrualProfileSchema(t, rows), [t, rows]);

    type FormValues = z.infer<typeof schema>;

    const defaultValues: FormValues = useMemo(() => ({
        name: '',
        description: '',
        accrual_code_id: null
    }), []);

    const { control, register, reset, handleSubmit, watch, formState: { errors, isDirty } } = useForm<FormValues>({
        defaultValues,
        resolver: zodResolver(schema),
        mode: 'onChange'
    });

    const nameValue = watch('name');
    const descriptionValue = watch('description');

    // Estado reglas y listas dinámicas (no parte del formulario principal)
    const [selectedRule, setSelectedRule] = useState<any>(null);
    const [selectedBalance, setSelectedBalance] = useState<any>(null);
    const [show, setShow] = useState<boolean>(false);

    // Listas desplegables (datos del servicio)
    const [accruals, setAccruals] = useState<any>([]);
    const [rulesAvailable, setRulesAvailable] = useState<any>([]);
    const [groupBalanceAvailable, setGroupBalanceAvailable] = useState<any>([]);

    // Listas de elementos agregados
    const [addedRules, setAddedRules] = useState<any>([]);
    const [addedGroupBalances, setAddedGroupBalances] = useState<any>([]);

    const timekeeperService = useMemo(() => new TimekeeperService(), []);

    const [loading, setLoading] = useState(false)
    const [alerts, setAlerts] = useState(false)

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const [accrualsData, rulesData, groupBalanceData] = await Promise.allSettled([
                    timekeeperService.accrual_code.getAll(),
                    timekeeperService.accrual_rule.getAll(),
                    timekeeperService.group_balance.getAll()
                ]);

                if (accrualsData.status === 'fulfilled') {
                    const accruals = accrualsData ? accrualsData.value : [];
                    setAccruals(accruals);
                }

                if (rulesData.status === 'fulfilled') {
                    const rules = rulesData ? rulesData.value : [];
                    setRulesAvailable(rules);
                }

                if (groupBalanceData.status === 'fulfilled') {
                    const groupBalance = groupBalanceData ? groupBalanceData.value : [];
                    setGroupBalanceAvailable(groupBalance);
                }

            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        }
        if (open) {
            fetchData();
            reset(defaultValues);
        }
    }, [open, timekeeperService, defaultValues, reset]);


    const changes = () => {
        if (
            isDirty ||
            addedRules.length > 0 ||
            addedGroupBalances.length > 0
        ) {
            setAlerts(true)
        } else {
            setOpen(false);
        }
    }

    const closeForm = () => {
        reset(defaultValues);
        setSelectedRule(null);
        setSelectedBalance(null);
        setShow(false);
        setAddedRules([]);
        setAddedGroupBalances([]);
        setOpen(false);
        setAlerts(false);
        setLoading(false);
    }

    const addAccrualRule = (ruleId?: number) => {
        const selectedRuleId = ruleId ?? selectedRule;

        if (selectedRuleId) {
            // Buscar la regla completa en rulesAvailable
            const ruleToAdd = rulesAvailable.find((rule: any) => rule.accrual_rule_id === selectedRuleId);

            if (ruleToAdd) {
                // Verificar que no esté ya agregado
                const alreadyAdded = addedRules.some((rule: any) => rule.accrual_rule_id === selectedRuleId);

                if (alreadyAdded) {
                    //enqueueSnackbar(t('accruals.accrual_profile.dialog.alerts.rule_already_added'), { variant: 'warning' });
                    return;
                }

                const newRuleToAdd = {
                    ...ruleToAdd,
                    show: show
                };
                setAddedRules([...addedRules, newRuleToAdd]);
                setSelectedRule(null);
                setShow(false);
            }
        }
    };

    const addGroupBalance = (groupBalanceId?: number) => {
        const selectedGroupBalanceId = groupBalanceId ?? selectedBalance;

        if (selectedGroupBalanceId) {
            // Buscar el grupo de balance completo en groupBalanceAvailable
            const balanceToAdd = groupBalanceAvailable.find((gb: any) => gb.group_balance_id === selectedGroupBalanceId);

            if (balanceToAdd) {
                // Verificar que no esté ya agregado
                const alreadyAdded = addedGroupBalances.some((gb: any) => gb.group_balance_id === selectedGroupBalanceId);

                if (alreadyAdded) {
                    //enqueueSnackbar(t('accruals.accrual_profile.dialog.alerts.balance_already_added'), { variant: 'warning' });
                    return;
                }

                setAddedGroupBalances([...addedGroupBalances, balanceToAdd]);
                setSelectedBalance(null);
            }
        }
    };

    const removeRule = (ruleId: number) => {
        const updatedRules = addedRules.filter((rule: any) => rule.accrual_rule_id !== ruleId);
        setAddedRules(updatedRules);
    }

    const removeGroupBalance = (groupBalanceId: number) => {
        const updatedBalances = addedGroupBalances.filter((gb: any) => gb.group_balance_id !== groupBalanceId);
        setAddedGroupBalances(updatedBalances);
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShow((event.target as HTMLInputElement).value === "true" ? true : false);
    };

    const onSubmit = async (data: FormValues) => {
        setLoading(true);

        const accrualProfileToCreate: AccrualProfileCreate = {
            name: data.name,
            description: data.description || null,
            accrual_code_id: data.accrual_code_id,
            accrual_rules_ids: addedRules.length > 0 ? addedRules.map((rule: any) => ({
                accrual_rule_id: rule.accrual_rule_id,
                show: rule.show
            })) : [],
            group_balances_ids: addedGroupBalances.length > 0 ? addedGroupBalances.map((gb: any) => gb.group_balance_id) : [],
        }

        console.log('Accrual Profile to create:', accrualProfileToCreate);

        try {

            await timekeeperService.accrual_profile.create(accrualProfileToCreate);

            enqueueSnackbar(t('accruals.accrual_profile.dialog.alerts.add.success'), { variant: 'success' });
            closeForm();
            reloading();

        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('accruals.accrual_profile.dialog.alerts.add.error'), { variant: 'error' });
            setLoading(false);
        }
    }

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                changes()
            }}
            disableRestoreFocus
            fullWidth
            maxWidth="md"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('accruals.accrual_profile.dialog.titles.add')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {alerts && (
                    <AlertCustom alerts={alerts} setAlerts={setAlerts} closeForm={closeForm} t={t('accruals.accrual_profile.dialog.alerts.unsaved_changes.title')} />
                )}
            </Box>
            <DialogContent dividers>
                <Box sx={{ mb: 3}}>
                    <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
                        {/* Campo Nombre */}
                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <TextField
                                fullWidth
                                label={t("accruals.accrual_profile.dialog.inputs.name")}
                                autoComplete='off'
                                {...register('name')}
                                error={!!errors.name}
                                helperText={errors.name?.message}
                                slotProps={{
                                    htmlInput: { maxLength: 100 },
                                    input: {
                                        endAdornment: <InputAdornment position="end">{`${nameValue?.length || 0}/100`}</InputAdornment>
                                    }
                                }}
                            />
                        </Grid>
                        {/* Campo Fondo de Acumulación */}
                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <Controller
                                name="accrual_code_id"
                                control={control}
                                render={({ field }) => (
                                    <Autocomplete
                                        options={accruals}
                                        getOptionLabel={(option: any) => option.name || ''}
                                        onChange={(event, value) => field.onChange(value ? value.accrual_code_id : null)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t("accruals.accrual_profile.dialog.inputs.accrual_fund")}
                                                error={!!errors.accrual_code_id}
                                                helperText={errors.accrual_code_id?.message}
                                            />
                                        )}
                                        loading={loading}
                                    />
                                )}
                            />
                        </Grid>
                        {/* Campo Descripción */}
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                            <TextField
                                fullWidth
                                label={t('accruals.accrual_profile.dialog.inputs.description')}
                                autoComplete='off'
                                {...register('description')}
                                error={!!errors.description}
                                helperText={errors.description?.message}
                                rows={2}
                                multiline
                                slotProps={{
                                    htmlInput: { maxLength: 255 },
                                    input: {
                                        endAdornment: <InputAdornment position="end">{`${descriptionValue?.length || 0}/255`}</InputAdornment>,
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>


                    <TableContainer component={Paper} sx={{ mt: 1 }}>
                        <Table size="small" sx={{ minWidth: 650, overflow: 'auto' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="left" width={30}>{t('accruals.accrual_profile.table.columns.actions')}</TableCell>
                                    <TableCell align='left' width={80}>{t('accruals.accrual_profile.dialog.inputs.rule')}</TableCell>
                                    <TableCell align='left' width={30}>{t('accruals.accrual_profile.dialog.inputs.show')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {/* Fila para agregar nueva regla */}
                                <TableRow>
                                    <TableCell align="left">
                                        <Typography variant="body2">-</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Autocomplete
                                            options={rulesAvailable.filter((rule: any) => !addedRules.some((addedRule: any) => addedRule.accrual_rule_id === rule.accrual_rule_id))}
                                            value={selectedRule}
                                            getOptionLabel={(option: any) => option.name || ''}
                                            isOptionEqualToValue={(option: any, value: any) => option.accrual_rule_id === value.accrual_rule_id}
                                            onChange={(event, value) => {
                                                setSelectedRule(value);

                                                if (value) {
                                                    addAccrualRule(value.accrual_rule_id);
                                                    setSelectedRule(null);
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    size='small'
                                                    label={t("accruals.accrual_profile.dialog.inputs.rule")}
                                                />
                                            )}
                                            disabled={loading}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <FormControl disabled={loading}>
                                            <RadioGroup
                                                row
                                                aria-labelledby="show-radio-group"
                                                name="show-radio-group"
                                                value={show}
                                                onChange={handleChange}
                                            >
                                                <FormControlLabel value="true" control={<Radio />} label={t("accruals.accrual_profile.dialog.inputs.visible")} />
                                                <FormControlLabel value="false" control={<Radio />} label={t("accruals.accrual_profile.dialog.inputs.hidden")} />
                                            </RadioGroup>
                                        </FormControl>
                                    </TableCell>
                                </TableRow>
                                {/* Filas de reglas agregadas */}
                                {addedRules.map((rule: any) => (
                                    <TableRow key={rule.accrual_rule_id}>
                                        <TableCell align="center">
                                            <IconButton
                                                size="small"
                                                onClick={() => removeRule(rule.accrual_rule_id)}
                                                color="error"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{rule.name}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {rule.show ? t("accruals.accrual_profile.dialog.inputs.visible") : t("accruals.accrual_profile.dialog.inputs.hidden")}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TableContainer component={Paper} sx={{ mt: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center" width={90}>{t('accruals.accrual_profile.table.columns.actions')}</TableCell>
                                    <TableCell align="center" width={400}>{t('accruals.accrual_profile.dialog.inputs.group_of_balance_sheets_with_domino_effect')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {/* Fila para agregar nuevo grupo de balance */}
                                <TableRow>
                                    <TableCell align="center">
                                        <Typography variant="body2">-</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Autocomplete
                                            options={groupBalanceAvailable.filter((groupBalance: any) => !addedGroupBalances.some((addedGroupBalance: any) => addedGroupBalance.group_balance_id === groupBalance.group_balance_id))}
                                            value={selectedBalance}
                                            getOptionLabel={(option: any) => option.name || ''}
                                            isOptionEqualToValue={(option: any, value: any) => option.group_balance_id === value.group_balance_id}
                                            onChange={(event, value) => {
                                                setSelectedBalance(value);

                                                if (value) {
                                                    addGroupBalance(value.group_balance_id);
                                                    setSelectedBalance(null);
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    size='small'
                                                    label={t('accruals.accrual_profile.dialog.inputs.group_of_balance_sheets_with_domino_effect')}
                                                />
                                            )}
                                            disabled={loading}
                                        />
                                    </TableCell>
                                </TableRow>
                                {/* Filas de grupos de balance agregados */}
                                {addedGroupBalances.map((gb: any) => (
                                    <TableRow key={gb.group_balance_id}>
                                        <TableCell align="center">
                                            <IconButton
                                                size="small"
                                                onClick={() => removeGroupBalance(gb.group_balance_id)}
                                                color="error"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{gb.name}</Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="error" onClick={() => changes()}>
                    {t('general.buttons.cancel')}
                </Button>
                <Button variant="contained" color="primary" onClick={handleSubmit(onSubmit)} disabled={loading || !isDirty} loading={loading}>
                    {t('general.buttons.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

